import VisibilityIcon from '@mui/icons-material/Visibility'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import axios from 'axios'
import React, { useCallback, useMemo, useState } from 'react'

import { LogDetailView } from './LogDetailView'

export interface ImportRowEntry {
	timestamp: string | null
	row_data: {
		ipsosId?: string
		result?: string
		definitionVersion?: number
		surveyUrl?: string
		sampleData?: Record<string, unknown>
		answers?: Record<string, { value?: unknown; type?: string; dataType?: string }>
	}
}

export interface LogEntry {
	_id?: string
	_index?: string
	timestamp?: string
	message?: string
	level?: number
	source?: string
	stack?: string
	batch_id?: string
}

export interface FieldworkImportDetailGridsProps {
	rows: ImportRowEntry[]
	logs: LogEntry[]
	logDetailLink?: string
}

const resultColor = (result: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
	switch (result) {
		case 'completed':
			return 'success'
		case 'expired':
			return 'warning'
		case 'failed':
		case 'error':
			return 'error'
		case 'pending':
			return 'info'
		default:
			return 'default'
	}
}

const levelColor = (level: number): 'error' | 'warning' | 'info' | 'default' => {
	if (level <= 3) {
		return 'error'
	}
	if (level === 4) {
		return 'warning'
	}
	if (level === 5) {
		return 'info'
	}
	return 'default'
}

export const FieldworkImportDetailGrids: React.FC<FieldworkImportDetailGridsProps> = ({
	rows,
	logs,
	logDetailLink,
}) => {
	const [detailOpen, setDetailOpen] = useState(false)
	const [detailLoading, setDetailLoading] = useState(false)
	const [detailError, setDetailError] = useState<string | null>(null)
	const [detailMessageId, setDetailMessageId] = useState<string | null>(null)
	const [detailData, setDetailData] = useState<Record<string, unknown> | null>(null)

	const openLogDetail = useCallback(
		async (messageId: string, index: string) => {
			setDetailOpen(true)
			setDetailMessageId(messageId)
			setDetailData(null)
			setDetailError(null)

			if (!logDetailLink) {
				setDetailError('Detail link is not configured.')
				return
			}

			if (!index) {
				setDetailError('Log index is missing.')
				return
			}

			setDetailLoading(true)
			try {
				const url = logDetailLink
					.replace('__INDEX__', encodeURIComponent(index))
					.replace('__MSGID__', encodeURIComponent(messageId))
				const response = await axios.get<{
					log: Record<string, unknown> | null
					error?: string
					debug?: Record<string, unknown>
				}>(url, {
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
				})
				if (response.data?.error) {
					const debugStr = response.data.debug ? ` (${JSON.stringify(response.data.debug)})` : ''
					setDetailError(`${response.data.error}${debugStr}`)
				} else {
					setDetailData(response.data?.log ?? null)
				}
			} catch (error) {
				const message = error instanceof Error ? error.message : 'Failed to fetch log detail'
				setDetailError(message)
			} finally {
				setDetailLoading(false)
			}
		},
		[logDetailLink],
	)

	const closeLogDetail = useCallback(() => {
		setDetailOpen(false)
		setDetailMessageId(null)
		setDetailData(null)
		setDetailError(null)
		setDetailLoading(false)
	}, [])

	const answerKeys = useMemo(() => {
		const keys = new Set<string>()
		rows.forEach((r) => {
			const answers = r.row_data?.answers ?? {}
			Object.keys(answers).forEach((k) => keys.add(k))
		})
		return Array.from(keys)
	}, [rows])

	const rowGridRows = useMemo(
		() =>
			rows.map((r, index) => {
				const data = r.row_data ?? {}
				const sample = (data.sampleData ?? {}) as Record<string, unknown>
				const answers = data.answers ?? {}
				const base: Record<string, unknown> = {
					id: index,
					timestamp: r.timestamp ?? '',
					ipsosId: data.ipsosId ?? '',
					result: data.result ?? '',
					email: (sample.email as string) ?? '',
					surveyUrl: data.surveyUrl ?? '',
					definitionVersion: data.definitionVersion ?? '',
				}
				answerKeys.forEach((key) => {
					base[`answer_${key}`] = answers[key]?.value ?? ''
				})
				return base
			}),
		[rows, answerKeys],
	)

	const rowColumns: GridColDef[] = useMemo(() => {
		const cols: GridColDef[] = [
			{ field: 'timestamp', headerName: 'timestamp', width: 180 },
			{
				field: 'ipsosId',
				headerName: 'ipsosId',
				width: 240,
				renderCell: (params) => (
					<Box sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{params.value as string}</Box>
				),
			},
			{
				field: 'result',
				headerName: 'result',
				width: 130,
				renderCell: (params) => {
					const value = (params.value as string) ?? ''
					return value ? <Chip label={value} color={resultColor(value)} size="small" /> : null
				},
			},
			{ field: 'email', headerName: 'email', width: 220 },
			{
				field: 'surveyUrl',
				headerName: 'surveyUrl',
				width: 280,
				renderCell: (params) => {
					const url = params.value as string
					if (!url) {
						return null
					}
					return (
						<Link href={url} target="_blank" rel="noopener" sx={{ fontSize: '0.85em' }}>
							{url}
						</Link>
					)
				},
			},
			{ field: 'definitionVersion', headerName: 'definitionVersion', width: 150, type: 'number' },
		]
		answerKeys.forEach((key) => {
			cols.push({ field: `answer_${key}`, headerName: key, width: 160 })
		})
		return cols
	}, [answerKeys])

	const logGridRows = useMemo(
		() =>
			logs.map((l, index) => ({
				id: index,
				messageId: l._id ?? '',
				logIndex: l._index ?? '',
				timestamp: l.timestamp ?? '',
				level: l.level ?? '',
				message: l.message ?? '',
				source: l.source ?? '',
			})),
		[logs],
	)

	const logColumns: GridColDef[] = useMemo(
		() => [
			{
				field: 'actions',
				headerName: '',
				width: 56,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				renderCell: (params) => {
					const row = params.row as { messageId?: string; logIndex?: string }
					const messageId = row.messageId ?? ''
					const logIndex = row.logIndex ?? ''
					if (!messageId) {
						return null
					}
					return (
						<IconButton
							size="small"
							onClick={() => openLogDetail(messageId, logIndex)}
							title="Show log detail"
							aria-label="Show log detail"
						>
							<VisibilityIcon fontSize="small" />
						</IconButton>
					)
				},
			},
			{ field: 'timestamp', headerName: 'timestamp', width: 180 },
			{
				field: 'level',
				headerName: 'level',
				width: 90,
				renderCell: (params) => {
					const level = Number(params.value)
					if (Number.isNaN(level)) {
						return null
					}
					return <Chip label={level} color={levelColor(level)} size="small" />
				},
			},
			{ field: 'message', headerName: 'message', flex: 1, minWidth: 240 },
			{ field: 'source', headerName: 'source', width: 160 },
		],
		[openLogDetail],
	)

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
			<Box>
				<Typography variant="subtitle1" sx={{ mb: 1 }}>
					import_rows ({rows.length})
				</Typography>
				<Box sx={{ height: 500, width: '100%' }}>
					<DataGridPro
						rows={rowGridRows}
						columns={rowColumns}
						disableRowSelectionOnClick
						density="compact"
						headerFilters
						columnHeaderHeight={42}
						headerFilterHeight={56}
						hideFooter
						sx={{
							'& .MuiDataGrid-headerFilterRow .MuiDataGrid-columnHeader': {
								paddingTop: 1,
								paddingBottom: 1,
								minHeight: '56px !important',
								maxHeight: '56px !important',
							},
						}}
					/>
				</Box>
			</Box>

			<Box>
				<Typography variant="subtitle1" sx={{ mb: 1 }}>
					graylog_logs ({logs.length})
				</Typography>
				<Box sx={{ height: 500, width: '100%' }}>
					<DataGridPro
						rows={logGridRows}
						columns={logColumns}
						disableRowSelectionOnClick
						density="compact"
						headerFilters
						hideFooter
					/>
				</Box>
			</Box>

			<Dialog open={detailOpen} onClose={closeLogDetail} fullWidth maxWidth="lg" scroll="paper">
				<DialogTitle>
					Log detail
					{detailMessageId && (
						<Typography
							component="span"
							variant="body2"
							sx={{ ml: 2, fontFamily: 'monospace', color: 'text.secondary' }}
						>
							{detailMessageId}
						</Typography>
					)}
				</DialogTitle>
				<DialogContent dividers>
					{detailLoading && (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					)}
					{!detailLoading && detailError !== null && <Alert severity="error">{detailError}</Alert>}
					{!detailLoading && detailError === null && detailData === null && (
						<Alert severity="info">No data returned.</Alert>
					)}
					{!detailLoading && detailError === null && detailData !== null && <LogDetailView data={detailData} />}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeLogDetail}>Close</Button>
				</DialogActions>
			</Dialog>
		</Box>
	)
}
